import { Injectable } from '@angular/core';
import { dateFormattingOptions } from '@domain/formatting';
import { LocaleValue } from '@hjc/masterdata-types';
import { ColDef, GridApi, ValueGetterParams } from 'ag-grid-community';
import { isValid, parseJSON } from 'date-fns';
import { Workbook } from 'exceljs';
import { isDate } from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class ExcelExportService {
  async exportGridToExcel<T>(api: GridApi<T>, columnDefs: ColDef[], fileName: string, lang: LocaleValue = 'fi'): Promise<void> {
    const exportData = this.getAllRowDataWithProcessedValues(api, columnDefs, lang);
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Data');

    if (exportData.length > 0) {
      const headers = Object.keys(exportData[0]);
      worksheet.addRow(headers);

      exportData.forEach(row => {
        worksheet.addRow(Object.values(row));
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private getAllRowDataWithProcessedValues<T>(api: GridApi<T>, columnDefs: ColDef[], lang: LocaleValue): Record<string, unknown>[] {
    const processedData: Record<string, unknown>[] = [];

    api.forEachNode(node => {
      if (node.data) {
        const processedRow: Record<string, unknown> = {};

        columnDefs.forEach(col => {
          if (col.headerName) {
            let cellValue: unknown;

            // Handle valueGetter columns
            if (col.valueGetter && typeof col.valueGetter === 'function') {
              const params = {
                data: node.data
              } as ValueGetterParams;
              cellValue = col.valueGetter(params);
            }
            // Handle valueFormatter columns
            else if (col.field) {
              cellValue = this.getFieldValue(node.data as Record<string, unknown>, col.field);
            }

            processedRow[col.headerName] = this.formatValueForExport(cellValue, lang);
          }
        });

        processedData.push(processedRow);
      }
    });

    return processedData;
  }

  private getFieldValue(obj: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce((current: unknown, key: string) => {
      return current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined;
    }, obj as unknown);
  }

  private formatValueForExport(value: unknown, lang: LocaleValue): unknown {
    if (Array.isArray(value)) {
      return value
        .map(item => {
          if (typeof item === 'object' && item !== null) {
            return JSON.stringify(item);
          }
          return String(item);
        })
        .join(', ');
    }
    if (isDate(value)) {
      return value.toLocaleDateString();
    }
    if (typeof value === 'string' && isValid(parseJSON(value))) {
      return new Date(value).toLocaleString(lang, dateFormattingOptions);
    }
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value);
    }
    return value;
  }
}
